import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../ui/dialog";
import { Plus, Trash2, Copy, ExternalLink, Gift, Music } from "lucide-react";
import { toast } from "sonner";

export default function RsvpRegistryTab({ ev, canEdit, reload }) {
  const eventId = ev.id;
  const [links, setLinks] = useState([]);
  const [songRequests, setSongRequests] = useState([]);
  const [rsvpEnabled, setRsvpEnabled] = useState(ev.rsvp_enabled !== false);
  const [rsvpMessage, setRsvpMessage] = useState(ev.rsvp_message || "");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", url: "", retailer: "", notes: "" });

  const publicUrl = `${window.location.origin}/rsvp/${eventId}`;

  const load = async () => {
    try {
      const [linksResp, songResp] = await Promise.all([
        api.get(`/events/${eventId}/registry`),
        api.get(`/events/${eventId}/song-requests`).catch(() => ({ data: [] })),
      ]);
      setLinks(linksResp.data);
      setSongRequests(songResp.data);
    } catch (e) { console.error("RSVP/Registry load failed:", e); }
  };
  useEffect(() => { load(); }, [eventId]);

  const saveSettings = async () => {
    try {
      await api.patch(`/events/${eventId}`, { rsvp_enabled: rsvpEnabled, rsvp_message: rsvpMessage });
      toast.success("RSVP settings saved");
      reload && reload();
    } catch (e) { toast.error("Could not save"); }
  };

  const copy = () => {
    navigator.clipboard.writeText(publicUrl);
    toast.success("Public RSVP link copied");
  };

  const add = async () => {
    if (!form.title || !form.url) { toast.error("Title & URL required"); return; }
    try {
      await api.post(`/events/${eventId}/registry`, form);
      setOpen(false);
      setForm({ title: "", url: "", retailer: "", notes: "" });
      load();
      toast.success("Registry link added");
    } catch (e) { toast.error("Could not add"); }
  };

  const del = async (id) => {
    if (!confirm("Delete this registry link?")) return;
    try { await api.delete(`/events/${eventId}/registry/${id}`); load(); }
    catch (e) { toast.error("Could not delete"); }
  };

  return (
    <div className="space-y-10">
      {/* RSVP microsite settings */}
      <section className="bevel border-hair rounded-sm p-6 md:p-8">
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted mb-2">MOD.RSVP / MICROSITE</div>
            <h3 className="font-display text-xl md:text-2xl font-bold text-app tracking-tight leading-none">Share your invitation</h3>
            <p className="text-sm text-muted mt-2">Guests visit this link, find their name, and RSVP — no account needed.</p>
          </div>
          <div className="flex items-center gap-2">
            <Switch
              data-testid="rsvp-enabled-switch"
              checked={rsvpEnabled}
              onCheckedChange={setRsvpEnabled}
              disabled={!canEdit}
            />
            <span className="text-sm text-app">{rsvpEnabled ? "Live" : "Closed"}</span>
          </div>
        </div>

        <div className="bg-canvas border border-hair rounded-sm p-4 flex items-center gap-3 mb-5">
          <code className="text-sm text-app font-light flex-1 truncate" data-testid="rsvp-public-url">{publicUrl}</code>
          <button onClick={copy} data-testid="copy-rsvp-link" className="text-muted hover:text-app flex items-center gap-1 text-xs">
            <Copy size={13} strokeWidth={1.5} /> Copy
          </button>
          <a href={publicUrl} target="_blank" rel="noreferrer" data-testid="preview-rsvp-link" className="text-muted hover:text-app flex items-center gap-1 text-xs">
            <ExternalLink size={13} strokeWidth={1.5} /> Preview
          </a>
        </div>

        <div>
          <Label className="eyebrow block mb-2">Welcome message (shown to guests)</Label>
          <Textarea
            data-testid="rsvp-message-input"
            rows={3}
            className="rounded-sm"
            value={rsvpMessage}
            onChange={(e) => setRsvpMessage(e.target.value)}
            placeholder="e.g. We can't wait to celebrate with the people we love most."
            disabled={!canEdit}
          />
        </div>

        {canEdit && (
          <div className="flex justify-end mt-5">
            <Button data-testid="save-rsvp-settings" onClick={saveSettings} className="rounded-sm bg-app hover:bg-[#000000]">
              Save settings
            </Button>
          </div>
        )}
      </section>

      {/* Registry */}
      <section>
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted mb-2">MOD.REGISTRY / GIFT_LINKS</div>
            <h3 className="font-display text-xl md:text-2xl font-bold text-app tracking-tight leading-none flex items-center gap-2">
              <Gift size={20} strokeWidth={1.5} /> <span className="font-mono tabular-nums">{links.length.toString().padStart(2, "0")}</span> {links.length === 1 ? "link" : "links"}
            </h3>
          </div>
          {canEdit && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-registry-btn" className="rounded-sm bg-app hover:bg-[#000000]">
                  <Plus size={16} strokeWidth={1.5} className="mr-2" /> Add link
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-surface rounded-sm max-w-md">
                <DialogHeader><DialogTitle className="font-serif text-2xl text-app">Add registry link</DialogTitle></DialogHeader>
                <div className="space-y-3 py-2">
                  <F label="Retailer (optional)">
                    <Input data-testid="registry-retailer-input" value={form.retailer} onChange={(e) => setForm({ ...form, retailer: e.target.value })} placeholder="e.g. Amazon, Zola, Crate & Barrel" />
                  </F>
                  <F label="Title">
                    <Input data-testid="registry-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Our wedding registry" />
                  </F>
                  <F label="URL">
                    <Input data-testid="registry-url-input" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://…" />
                  </F>
                  <F label="Note (optional)">
                    <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                  </F>
                </div>
                <DialogFooter>
                  <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
                  <Button data-testid="registry-submit" onClick={add} className="rounded-sm bg-app hover:bg-[#000000]">Add</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {links.length === 0 ? (
          <div className="bevel border-hair rounded-sm p-10 text-center">
            <div className="font-serif text-2xl text-app mb-2">No registry links yet.</div>
            <p className="text-muted font-light">Add your Amazon, Zola, or honeymoon fund links — they&apos;ll appear on your RSVP microsite.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {links.map(l => (
              <div key={l.id} data-testid={`registry-item-${l.id}`} className="bevel border-hair rounded-sm p-5 card-lift flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  {l.retailer && <div className="eyebrow mb-1">{l.retailer}</div>}
                  <div className="font-serif text-xl text-app truncate">{l.title}</div>
                  <a href={l.url} target="_blank" rel="noreferrer" className="text-xs text-muted hover:text-app block truncate mt-1">{l.url}</a>
                  {l.notes && <p className="text-sm text-muted font-light mt-2">{l.notes}</p>}
                </div>
                {canEdit && (
                  <button onClick={() => del(l.id)} data-testid={`delete-registry-${l.id}`} className="text-muted hover:text-[#EF4444] shrink-0">
                    <Trash2 size={14} strokeWidth={1.5} />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Song requests */}
      <section>
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="eyebrow mb-1">From the dance floor</div>
            <div className="font-serif text-2xl text-app flex items-center gap-2">
              <Music size={20} strokeWidth={1.5} /> {songRequests.length} song requests
            </div>
          </div>
          <div className="text-xs text-muted font-light max-w-xs text-right">
            These land automatically on your DJ / Band vendor&apos;s portal.
          </div>
        </div>

        {songRequests.length === 0 ? (
          <div className="bevel border-hair rounded-sm p-8 text-center">
            <p className="text-muted font-light">No song requests yet — guests can add them when they RSVP.</p>
          </div>
        ) : (
          <div className="bevel border-hair rounded-sm divide-y divide-[color:var(--hair)]/60">
            {songRequests.map(s => (
              <div key={s.id} data-testid={`song-request-row-${s.id}`} className="p-4 grid grid-cols-[1fr_120px] gap-3">
                <div>
                  <div className="text-app">{s.song_request}</div>
                  <div className="text-xs text-muted font-light mt-0.5">Requested by {s.name}</div>
                </div>
                <div className="text-right">
                  <span className={`eyebrow px-2 py-0.5 rounded-sm ${
                    s.rsvp_status === "yes" ? "bg-surface-2 text-[#059669]" :
                    s.rsvp_status === "no" ? "bg-[#EF4444]/10 text-[#EF4444]" :
                    "bg-canvas text-muted"
                  }`}>{s.rsvp_status}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function F({ label, children }) {
  return (<div><Label className="eyebrow block mb-2">{label}</Label>{children}</div>);
}
